import type { FeatureCollection } from 'geojson';
import { DATA_SOURCE_TYPES } from '@/components/maplibre/consts';
import type { ColumnMetadata, GeoJSONDataSourceConfig } from '@/components/maplibre/types';
import { getGeometryTypes } from '@/components/maplibre/utils/maplibre-utils';
import { getMultipleLayersGeoJson } from '@/app/actions/geojson';
import type { Layer, MapLayer } from '@/lib/db-row-types';
import { getLayerStyle } from '@/utils/layer-sources/style-processor';

/**
 * Builds GeoJSON data source configurations from layers
 */
export async function buildGeoJsonDataSources(
  layers: Layer[],
  mapLayers: MapLayer[] = [],
  columnMetadataMap: Map<string, ColumnMetadata[]> = new Map()
): Promise<GeoJSONDataSourceConfig[]> {
  if (layers.length === 0) {
    return [];
  }

  try {
    const layerIds = layers.map(l => l.id);
    const response = await getMultipleLayersGeoJson(layerIds);

    if (!response.success || !response.data) {
      console.error('Failed to fetch GeoJSON:', response.error);
      return [];
    }

    return await Promise.all(
      response.data.map(async (geoJsonData: FeatureCollection, index: number) => {
        const layer = layers[index];
        const mapLayer = mapLayers.find(ml => ml.layer_id === layer.id);
        const columnMetadata = columnMetadataMap.get(layer.id);
        const style = await getLayerStyle(mapLayer, layer, columnMetadata);
        const geometryTypes = getGeometryTypes(geoJsonData);

        return {
          id: layer.id,
          name: layer.title || 'Untitled Layer',
          type: DATA_SOURCE_TYPES.GEOJSON,
          data: geoJsonData,
          geometryTypes,
          visible: true,
          style,
          columnMetadata,
        };
      })
    );
  } catch (error) {
    console.error('Error fetching GeoJSON data:', error);
    return [];
  }
}
